import { checkResponsibility } from '../validation/isinValidation';
import { PermissionInterface } from '../api/commonFunctions';
import DigitalAssetStatus from '../model/DigitalAssetStatus';

export interface DigitalAssetFieldPermissions {
    isXtIsin: boolean;
    canEditNnaControlledFields: boolean;
    canEditDtiControlledFields: boolean;
    dnaLocksDigitalStatusToYDtif: boolean;
    nnaMustSetStatusToYNna: boolean;
    nnaCannotSetStatusToYDtif: boolean;
    generalFieldsDisabled: boolean;
}

/**
 * Role- and jurisdiction-based editability of the 5 Digital-Asset-/DTI fields (see
 * DTI_FIELD_EDIT_PERMISSIONS_ANALYSIS.md §7/§8/§12). Server-side enforcement is authoritative;
 * this only controls the GUI presentation (editable vs. read-only/disabled).
 *
 * Extracted out of IsinFields as a pure function so it can be unit-tested directly, without
 * mounting the (large, network-calling) IsinFields component tree.
 *
 * Deliberately a pure function of the CURRENT (final) values only - no side effects, no field
 * auto-correction. `nnaMustSetStatusToYNna`/`nnaCannotSetStatusToYDtif` are meant to be read by a plain
 * `validate()` function that blocks submission with an error message; nothing here ever mutates form
 * state. This avoids the earlier bug where an effect-driven auto-reset of Digital Status did not undo
 * itself when Ledger Name/Tokenization Level were changed and then changed back within the same edit -
 * since nothing is written here, an undone change simply never triggers a violation in the first place.
 *
 * @param persistedDigitalAssetStatus the Digital Status value as currently persisted (i.e. the record's
 * value before this edit). A record that already carries Y_DTIF (e.g. set earlier by ROLE_DNA) may still
 * be resubmitted unchanged by an NNA editing an unrelated field. Defaults to undefined so existing
 * callers/tests are unaffected.
 * @param nnaOwnedFieldsChanged whether Distributed Ledger Name or Digital Asset Tokenization Level's
 * CURRENT value differs from the PERSISTED record's value (a plain final-value comparison, not "was ever
 * touched during this edit") - mirrors the inbound business rule (DtiInboundMergePolicy.
 * applyForNnaUpdate): submitting/updating these 2 NNA-owned fields always (re-)attests Y_NNA, even
 * overriding a Y_DTIF value set earlier by ROLE_DNA - the DTIF assignment is presumed stale once the NNA
 * touches the underlying digital-asset data again. If Ledger Name/Tokenization Level are edited and then
 * reverted back to their original value within the same edit, this is (correctly) false again. Defaults
 * to false so existing callers/tests are unaffected.
 */
export function computeDigitalAssetFieldPermissions(
    currentIsin: string | undefined,
    permissions: Partial<PermissionInterface> | undefined,
    persistedDigitalAssetStatus: string | undefined = undefined,
    nnaOwnedFieldsChanged: boolean = false
): DigitalAssetFieldPermissions {
    const isXtIsin = typeof currentIsin === 'string' && currentIsin.startsWith('XT');
    const isAdmin = !!permissions?.admin;
    const isDna = !!permissions?.dna;
    const isResponsibleNna = !!permissions?.nna && checkResponsibility(currentIsin);
    const isRealNna = !isAdmin && isResponsibleNna;

    // NNA-controlled fields (Digital Status free edit, Distributed Ledger Name, Digital Asset Tokenization Level)
    const canEditNnaControlledFields = isAdmin || isResponsibleNna;
    // DTI-controlled fields (Equivalent Digital Token Group, Digital Token Identifier(s))
    const canEditDtiControlledFields = isAdmin || isDna || (isXtIsin && isResponsibleNna);
    // ROLE_DNA may set Digital Status, but only ever to the fixed value Y_DTIF (§3)
    const dnaLocksDigitalStatusToYDtif = isDna && !canEditNnaControlledFields;
    // A real NNA (non-XT or XT-NNA, never admin) that is (re-)submitting Ledger Name/Tokenization Level
    // must explicitly declare Y_NNA - mirrors DtiInboundMergePolicy.applyForNnaUpdate, which never
    // preserves the old status once those 2 NNA-owned fields are resubmitted, regardless of what the old
    // value was (including an existing Y_DTIF).
    const nnaMustSetStatusToYNna = isRealNna && nnaOwnedFieldsChanged;
    // If Ledger Name/Tokenization Level are untouched, an NNA must still not be able to newly attest
    // Y_DTIF out of nothing - mirrors the inbound "Y" -> Y_NNA derivation (DigitalAssetStatusDeriver). A
    // record already Y_DTIF may pass through unchanged (no violation) since nnaMustSetStatusToYNna above
    // already covers the "fields changed" case exclusively.
    const nnaCannotSetStatusToYDtif = isRealNna && !nnaOwnedFieldsChanged
            && persistedDigitalAssetStatus !== DigitalAssetStatus.Y_DTIF;
    // ROLE_DNA may only ever touch the 2 DTI-controlled fields (+ Digital Status → Y_DTIF): every other,
    // general ISIN field must stay read-only for a pure DNA user, identically for XT and non-XT ISINs.
    // GUI-only convenience; the binding enforcement is the server-side field-permission check.
    const generalFieldsDisabled = isDna && !canEditNnaControlledFields;

    return {
        isXtIsin,
        canEditNnaControlledFields,
        canEditDtiControlledFields,
        dnaLocksDigitalStatusToYDtif,
        nnaMustSetStatusToYNna,
        nnaCannotSetStatusToYDtif,
        generalFieldsDisabled
    };
}
